import { Copy, Share2 } from "lucide-preact";
import type { ComponentType } from "preact";
import { useEffect, useState } from "preact/hooks";
import { FacebookIcon, LinkedInIcon, type SocialIconProps, ThreadsIcon, TwitterIcon } from "../icons";
import { Modal } from "../Modal";
import { RedditIcon, WhatsAppIcon } from "./ShareIcons";

export type ShareModalProps = {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title: string;
  text?: string;
  articleId?: string;
  authorAlias?: string;
};

type Target = { id: string; label: string; icon: ComponentType<SocialIconProps>; href: (url: string, title: string) => string };

const TARGETS: Target[] = [
  { id: "twitter", label: "X (Twitter)", icon: TwitterIcon, href: (url, title) => `https://twitter.com/intent/tweet?url=${url}&text=${title}` },
  { id: "linkedin", label: "LinkedIn", icon: LinkedInIcon, href: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
  { id: "reddit", label: "Reddit", icon: RedditIcon, href: (url, title) => `https://reddit.com/submit?url=${url}&title=${title}` },
  { id: "facebook", label: "Facebook", icon: FacebookIcon, href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${url}` },
  { id: "whatsapp", label: "WhatsApp", icon: WhatsAppIcon, href: (url) => `https://wa.me/?text=${url}` },
  { id: "threads", label: "Threads", icon: ThreadsIcon, href: (url, title) => `https://www.threads.net/intent/post?text=${title}%20${url}` },
];

const BUTTON_CLASS =
  "flex items-center justify-center space-x-2 p-3 bg-white dark:bg-neutral-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors";

/** The ways to share a link: a window per network, the device's own share sheet when it has one, and a copy. */
const useShare = ({ url, title, text, onClose, articleId, authorAlias }: Omit<ShareModalProps, "isOpen">) => {
  const [copied, setCopied] = useState(false);
  const [hasNativeShare, setHasNativeShare] = useState(false);

  useEffect(() => {
    setHasNativeShare(typeof navigator !== "undefined" && navigator.share !== undefined);
  }, []);

  return {
    copied,
    hasNativeShare,
    open: (target: Target) => {
      window.open(target.href(encodeURIComponent(url), encodeURIComponent(title)), "_blank", "width=600,height=400");
      onClose();
    },
    shareNatively: () => {
      navigator.share({ title, text: text || title, url }).catch((error: unknown) => console.error("Failed to share", error));
      onClose();
    },
    copy: () => {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      });
    },
  };
};

export const ShareModal = ({ isOpen, onClose, url, title, text, articleId, authorAlias }: ShareModalProps) => {
  const share = useShare({ url, title, text, onClose, articleId, authorAlias });

  return (
    <Modal isOpen={isOpen} onClose={onClose} hideCloseButton>
      <div class="grid grid-cols-2 gap-3" data-test-article={articleId} data-test-author={authorAlias}>
        {TARGETS.map((target) => (
          <button
            key={target.id}
            type="button"
            onClick={() => share.open(target)}
            class={`${BUTTON_CLASS} text-gray-900 dark:text-gray-100`}
            data-test-click="share"
            data-test-platform={target.id}
          >
            <target.icon size={20} color="monochrome" />
            <span class="text-sm font-medium">{target.label}</span>
          </button>
        ))}
        {share.hasNativeShare && (
          <button type="button" onClick={share.shareNatively} class={BUTTON_CLASS} data-test-click="share" data-test-platform="native">
            <Share2 size={20} class="text-gray-900 dark:text-gray-100" />
            <span class="text-sm font-medium text-gray-900 dark:text-gray-100">More</span>
          </button>
        )}
        <button type="button" onClick={share.copy} class={BUTTON_CLASS} data-test-click="share" data-test-platform="copy">
          <Copy size={20} class="text-gray-900 dark:text-gray-100" />
          <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{share.copied ? "Copied!" : "Copy Link"}</span>
        </button>
      </div>
    </Modal>
  );
};
